import React, { useEffect, useId, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

// components
import Slider from '../../atoms/Slider/Slider'
import Input from '../../atoms/Input/Input'
import InputLabel from '../../atoms/InputLabel/InputLabel'
import FormItem from '../../atoms/FormItem/FormItem'

// utils
import { formatNumber, formatPrice, getSecondaryPrice, parseTextValueToNumberValue } from '../../utils/helper'

// styles
import * as SC from './PriceRangeFormFieldStyles'

// types
import { PriceRangeFormFieldProps, PriceRangeFormFieldValueType } from './types'

// hooks
import useMessages from '../../hooks/useMessages'

// component
const PriceRangeFormField = (props: PriceRangeFormFieldProps) => {
	const {
		input: { value, onChange },
		priceFromDefaultValue,
		priceToDefaultValue,
		currency,
		priceToSecondaryDefaultValue,
		currencySecondary,
		priceStep = 1,
		onAfterChange
	} = props

	const { messages } = useMessages()
	const { formatMessage } = useIntl()
	const priceFromId = useId()
	const priceToId = useId()

	// default value
	const priceSliderDefaultValue = useMemo(
		() => (priceFromDefaultValue !== undefined && priceToDefaultValue !== undefined ? [priceFromDefaultValue, priceToDefaultValue] : undefined),
		[priceFromDefaultValue, priceToDefaultValue]
	)

	// submitted value
	const priceFromValue = value.priceFrom ?? priceFromDefaultValue
	const priceToValue = value.priceTo ?? priceToDefaultValue
	const priceSliderValue = useMemo(
		() => (value.priceFrom !== undefined && value.priceTo !== undefined ? [value.priceFrom, value.priceTo] : priceSliderDefaultValue),
		[value.priceFrom, value.priceTo, priceSliderDefaultValue]
	)

	const [serviceTotalPriceValues, setServiceTotalPriceValues] = useState<{
		priceFrom: number | undefined
		priceTo: number | undefined
		priceSlider: number[] | undefined
	}>({
		priceFrom: priceFromValue,
		priceTo: priceToValue,
		priceSlider: priceSliderValue
	})

	const { priceFrom, priceTo, priceSlider } = serviceTotalPriceValues

	const secondaryPriceFrom = getSecondaryPrice({
		primaryValue: priceFrom,
		primaryMaxValue: priceToDefaultValue,
		secondaryMaxValue: priceToSecondaryDefaultValue
	})

	const secondaryPriceTo = getSecondaryPrice({
		primaryValue: priceTo,
		primaryMaxValue: priceToDefaultValue,
		secondaryMaxValue: priceToSecondaryDefaultValue
	})

	const hasSecondaryPrice = typeof secondaryPriceFrom === 'number' && typeof secondaryPriceTo === 'number' && currencySecondary

	const disabled = priceFromDefaultValue === undefined || priceToDefaultValue === undefined

	useEffect(() => {
		setServiceTotalPriceValues({
			priceFrom: priceFromValue,
			priceTo: priceToValue,
			priceSlider: priceSliderValue
		})
	}, [priceFromValue, priceToValue, priceSliderValue])

	const handleChange = (newValues: PriceRangeFormFieldValueType) => {
		onChange(newValues)
		if (onAfterChange) {
			onAfterChange(newValues)
		}
	}

	const onChangePriceFrom: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		const newValue = parseTextValueToNumberValue(e.target.value)
		setServiceTotalPriceValues((prevValues) => ({ ...prevValues, priceFrom: newValue }))
	}

	const onBlurPriceFrom: React.FocusEventHandler<HTMLInputElement> = (e) => {
		if (priceTo === undefined || priceFromDefaultValue === undefined) {
			return
		}

		let newValue = parseTextValueToNumberValue(e.target.value)

		// validate value
		if (newValue >= priceTo || newValue <= priceFromDefaultValue || !newValue) {
			newValue = priceFromDefaultValue
		}

		// update internal state
		setServiceTotalPriceValues((prevValues) => ({
			...prevValues,
			priceFrom: newValue,
			priceTo,
			priceSlider: [newValue, priceTo]
		}))

		// update parent state only when value change
		if (priceFromValue !== newValue) {
			handleChange({ priceFrom: newValue, priceTo })
		}
	}

	const onChangePriceTo: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		const newValue = parseTextValueToNumberValue(e.target.value)
		setServiceTotalPriceValues((prevValues) => ({ ...prevValues, priceTo: newValue }))
	}

	const onBlurPriceTo: React.FocusEventHandler<HTMLInputElement> = (e) => {
		if (priceFrom === undefined || priceToDefaultValue === undefined) {
			return
		}

		let newValue = parseTextValueToNumberValue(e.target.value)

		// validate value
		if (priceFrom >= newValue || priceToDefaultValue <= newValue || !newValue) {
			newValue = priceToDefaultValue
		}

		// update internal state
		setServiceTotalPriceValues((prevValues) => ({
			...prevValues,
			priceFrom,
			priceTo: newValue,
			priceSlider: [priceFrom, newValue]
		}))

		// update parent state only when value change
		if (priceToValue !== newValue) {
			handleChange({ priceFrom, priceTo: newValue })
		}
	}

	return (
		<FormItem asFieldset label={messages.Price} labelScreenReaderOnly>
			<SC.PriceFieldWrapper>
				<SC.PriceInputsWrapper>
					<SC.PriceInputWrapper>
						<InputLabel htmlFor={priceFromId} screenReaderOnly>
							{formatMessage({ id: 'Price from', defaultMessage: 'Price from' })}
						</InputLabel>
						<Input
							id={priceFromId}
							inputMode={'numeric'}
							type={'text'}
							suffix={currency.symbol}
							isPristine={priceFrom === priceFromDefaultValue}
							disabled={disabled}
							value={formatNumber(priceFrom)}
							onChange={onChangePriceFrom}
							onBlur={onBlurPriceFrom}
						/>
						{hasSecondaryPrice && (
							<SC.SecondaryPrice>{formatPrice({ from: secondaryPriceFrom, currency: currencySecondary?.code })}</SC.SecondaryPrice>
						)}
					</SC.PriceInputWrapper>
					<SC.PriceInputDivider>{'-'}</SC.PriceInputDivider>
					<SC.PriceInputWrapper>
						<InputLabel htmlFor={priceToId} screenReaderOnly>
							{formatMessage({ id: 'Price to', defaultMessage: 'Price to' })}
						</InputLabel>
						<Input
							id={priceToId}
							inputMode={'numeric'}
							type={'text'}
							suffix={currency.symbol}
							isPristine={priceTo === priceToDefaultValue}
							value={formatNumber(priceTo)}
							disabled={disabled}
							onChange={onChangePriceTo}
							onBlur={onBlurPriceTo}
						/>
						{hasSecondaryPrice && (
							<SC.SecondaryPrice>{formatPrice({ from: secondaryPriceTo, currency: currencySecondary?.code })}</SC.SecondaryPrice>
						)}
					</SC.PriceInputWrapper>
				</SC.PriceInputsWrapper>
				<Slider
					range
					min={priceFromDefaultValue}
					max={priceToDefaultValue}
					step={priceStep}
					value={priceSlider}
					allowCross={false}
					pushable={1}
					disabled={disabled}
					onChange={(newValue) => {
						// update internal state on every change
						if (Array.isArray(newValue)) {
							setServiceTotalPriceValues({
								priceFrom: newValue[0],
								priceTo: newValue[1],
								priceSlider: newValue
							})
						}
					}}
					onChangeComplete={(newValues) => {
						// update parent state only when user stopped dragging button and value are different from current parent state
						if (Array.isArray(newValues) && (newValues[0] !== priceFromValue || newValues[1] !== priceToValue)) {
							handleChange({ priceFrom: newValues[0], priceTo: newValues[1] })
						}
					}}
				/>
			</SC.PriceFieldWrapper>
		</FormItem>
	)
}

export default React.memo(PriceRangeFormField)
